/*
 * Purpose: Compose Create Password UI from hook state and stateless controls.
 * Boundary: Layout only; delegates generation and validation to hook.
 * Invariants:
 * - Consumes object h from useCreatePassword with all state and handlers.
 * - ASCII only; no Unicode characters in comments or strings.
 */

// Imports
import PasswordRulesChips from "./createpassword-chips";
import { PasswordInput, ConfirmInput, TypeSelector, LengthInput, GenerateButton, HideCopyButton } from "./createpassword-controls";

const heading = "Create Password";

// Component CreatePasswordLayout:
// Stateless layout for password input, no OTP
export default function CreatePasswordLayout({ ...h }) {
    // Rendering - form structure without OTP
    return (
        <div className="space-y-3 md:space-y-6" aria-labelledby="create-password-title">

            {/* Heading */}
            {h.showHeading && (
                <h1 id="create-password-title" className="text-base md:text-l text-neutral-300">
                    {heading}
                </h1>
            )}

            {/* Rule Chips - show compliance counts */}
            <PasswordRulesChips
                password={h.password}
                confirmPassword={h.confirmPassword}
            />

            {/* Password inputs and buttons */}
            <div className="space-y-4">
                {/* Password input */}
                <div>
                    <PasswordInput
                        id="password"
                        value={h.password}
                        onChange={h.onPasswordChange}
                        showPassword={h.showPassword}
                    />
                    {/* Buttons below password */}
                    {!h.showSelectors && (
                        <div className="flex gap-2 mt-2">
                            <GenerateButton onClick={h.generatePassword} disabled={!!h.status} />
                            <HideCopyButton
                                onToggleShow={h.toggleShow}
                                onCopy={h.onCopy}
                                showPassword={h.showPassword}
                            />
                        </div>
                    )}
                </div>

                {/* Confirm password input */}
                <ConfirmInput
                    id="confirm"
                    value={h.confirmPassword}
                    onChange={h.onConfirmChange}
                    showPassword={h.showPassword}
                />
            </div>

            {/* Generator Controls */}
            {h.showSelectors && (
                <div className="rounded-xl bg-neutral-950 ring-1 ring-neutral-800 p-5">
                    <div className="grid md:grid-cols-[auto_1fr_auto] gap-4 items-end">
                        <TypeSelector
                            value={h.genType}
                            onChange={(e) => h.setGenType(e.target.value)}
                        />
                        <LengthInput
                            type={h.genType}
                            value={h.length}
                            onChange={(e) =>
                                h.setLength(
                                    Number(e.target.value) || (h.genType === "chars" ? 20 : 4)
                                )
                            }
                        />
                        <GenerateButton
                            onClick={h.generatePassword}
                            disabled={!!h.status}
                        />
                    </div>
                    {h.status && (
                        <p className="mt-4 text-sm text-neutral-400">{h.status}</p>
                    )}
                </div>
            )}
        </div>
    );
}
